import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Filter,
  Check,
  Plus,
  Edit2,
  Trash2,
  Lock,
  Globe,
  X,
  Sparkles,
} from "lucide-react";
import { useSavedFilters } from "./useSavedFilters";
import { AdvancedFilterModal } from "./AdvancedFilterModal";
import type { AdvancedFilter, EntityType } from "./filterTypes";
import {
  SAVED_FILTERS_STORAGE_KEY,
  ACTIVE_FILTER_STORAGE_KEY,
  defaultFilters,
} from "./useSavedFilters";

export function AdvancedFilterDropdown({
  onPreviewFilter,
  storageKey = SAVED_FILTERS_STORAGE_KEY,
  activeKey = ACTIVE_FILTER_STORAGE_KEY,
  fallbackFilters = defaultFilters,
  defaultEntity = "deal",
  title = "Filtros",
}: {
  onPreviewFilter?: (filter: AdvancedFilter | null) => void;
  storageKey?: string;
  activeKey?: string;
  fallbackFilters?: AdvancedFilter[];
  defaultEntity?: EntityType;
  title?: string;
}) {
  const {
    filters,
    activeFilter,
    activeFilterId,
    setActiveFilterId,
    addFilter,
    updateFilter,
    deleteFilter,
  } = useSavedFilters(storageKey, activeKey, fallbackFilters);

  const [modalOpen, setModalOpen] = useState(false);
  const [filterToEdit, setFilterToEdit] = useState<AdvancedFilter | null>(null);

  const handleCreateNew = () => {
    setFilterToEdit(null);
    setModalOpen(true);
  };

  const handleEditFilter = (filter: AdvancedFilter, e: React.MouseEvent) => {
    e.stopPropagation();
    setFilterToEdit(filter);
    setModalOpen(true);
  };

  const handleDeleteFilter = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("Deseja realmente excluir este filtro salvo?")) {
      deleteFilter(id);
    }
  };

  const handleSaveFilter = (
    filterData: Omit<AdvancedFilter, "id" | "createdAt" | "updatedAt"> & {
      id?: string;
    },
  ) => {
    if (filterData.id) {
      updateFilter(filterData.id, filterData);
      setActiveFilterId(filterData.id);
    } else {
      addFilter(filterData);
    }
  };

  const handleClearActive = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveFilterId(null);
    onPreviewFilter?.(null);
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`h-9 px-3 gap-2 font-medium cursor-pointer border-border/80 transition-all ${
                activeFilter
                  ? "bg-primary/10 border-primary/40 text-primary hover:bg-primary/15"
                  : "bg-background hover:bg-muted/60"
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              <span className="text-xs truncate max-w-[160px]">
                {activeFilter ? activeFilter.name : title}
              </span>
              {activeFilter && (
                <Badge
                  variant="secondary"
                  className="h-4 px-1 text-[10px] bg-primary text-primary-foreground font-bold"
                >
                  Ativo
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start" className="w-72 p-1 text-xs">
            <DropdownMenuLabel className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1.5">
              Filtro ativo
            </DropdownMenuLabel>

            {/* Opção Todos os Negócios (Sem Filtro) */}
            <DropdownMenuItem
              onClick={() => {
                setActiveFilterId(null);
                onPreviewFilter?.(null);
              }}
              className="flex items-center justify-between px-2 py-2 cursor-pointer font-medium"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-muted-foreground" />
                <span>Todos os Negócios</span>
              </div>
              {!activeFilter && <Check className="w-4 h-4 text-primary" />}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1">
              Filtros Salvos
            </DropdownMenuLabel>

            {filters.length === 0 ? (
              <div className="px-2 py-2 text-xs text-muted-foreground italic">
                Nenhum filtro personalizado salvo.
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto space-y-0.5">
                {filters.map((f) => {
                  const isSelected = activeFilterId === f.id;
                  return (
                    <DropdownMenuItem
                      key={f.id}
                      onSelect={() => {
                        setActiveFilterId(f.id);
                        onPreviewFilter?.(null);
                      }}
                      className={`flex items-center justify-between px-2 py-1.5 rounded-md cursor-pointer group transition-colors ${
                        isSelected
                          ? "bg-accent text-accent-foreground font-semibold"
                          : "hover:bg-muted/80 text-foreground"
                      }`}
                    >
                      <div className="flex items-center gap-2 truncate flex-1 mr-2">
                        {f.visibility === "private" ? (
                          <Lock className="w-3 h-3 text-muted-foreground shrink-0" />
                        ) : (
                          <Globe className="w-3 h-3 text-muted-foreground shrink-0" />
                        )}
                        <span className="truncate">{f.name}</span>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        {isSelected && (
                          <Check className="w-3.5 h-3.5 text-primary mr-1" />
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditFilter(f, e);
                          }}
                          title="Editar filtro"
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-background rounded text-muted-foreground hover:text-foreground transition-opacity"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFilter(f.id, e);
                          }}
                          title="Excluir filtro"
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-background rounded text-muted-foreground hover:text-destructive transition-opacity"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </div>
            )}

            <DropdownMenuSeparator />

            {/* Criar Novo Filtro */}
            <DropdownMenuItem
              onClick={handleCreateNew}
              className="flex items-center gap-2 px-2 py-2 text-primary font-semibold cursor-pointer hover:bg-primary/10"
            >
              <Plus className="w-4 h-4" />
              <span>Criar novo filtro...</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Botão rápido para remover filtro ativo */}
        {activeFilter && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClearActive}
            className="h-8 w-8 text-muted-foreground hover:text-foreground cursor-pointer"
            title="Limpar filtro ativo"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      <AdvancedFilterModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        filterToEdit={filterToEdit}
        defaultEntity={defaultEntity}
        onSaveFilter={handleSaveFilter}
        onPreviewFilter={(previewFilter) => {
          onPreviewFilter?.(previewFilter);
        }}
      />
    </>
  );
}
